import { useMemo } from 'react';

import { Media } from '@media-viewer';
import { useAtom, useAtomValue } from 'jotai';

import { PlaybackCell, VIDEO_TYPE } from '@blocks';
import { useTranslate } from '@i18n';
import { activeUserSettingsAtom, activeVideoAtom, cameraViewsAtom, comparisonVideoAtom } from '@store';
import { useMatchMedia } from '@ui';

import { MediaVariant, mediaVariants } from './MediaVariant';
import * as css from './PlaybackScreen.css';

export type StatusPaneProps = {
    text: string;
    width: number;
    height: number;
};

type VideoPlaybackProps = {
    displayMode: 'comparison' | 'playback';
    variant: MediaVariant;
};

const videoCountMap = {
    [mediaVariants.SINGLE]: 1,
    [mediaVariants.DOUBLE]: 2,
    [mediaVariants.FULL]: 4,
} as const satisfies Record<MediaVariant, number>;

export function PlaybackVideo({ displayMode, variant }: VideoPlaybackProps) {
    const [cameraViews, setCameraViews] = useAtom(cameraViewsAtom);
    const activeVideos = useAtomValue(activeVideoAtom);
    const { comparisonSwingId } = useAtomValue(activeUserSettingsAtom);
    const comparisonVideos = useAtomValue(comparisonVideoAtom);

    const t = useTranslate();

    const isComparison = displayMode === 'comparison' && !!comparisonSwingId;

    const videoCount = videoCountMap[variant] ?? 1;

    const singleVideo = videoCount === 1;

    const videos = useMemo(
        () =>
            Array.from({ length: videoCount })
                .map((_, index) => {
                    const viewIndex = isComparison ? Math.floor(index / 2) : index;
                    const view = cameraViews[viewIndex] ?? 1;
                    const isComparisonVideo = (isComparison && index % 2 === 1) || (isComparison && singleVideo);
                    const videosForAngle = isComparisonVideo ? comparisonVideos : activeVideos;
                    const video = videosForAngle?.find((video) => video?.view === view);
                    return video
                        ? {
                              viewIndex,
                              type: isComparisonVideo ? VIDEO_TYPE.COMPARISON : VIDEO_TYPE.CURRENT,
                              ...video,
                          }
                        : undefined;
                })
                .filter((v) => !!v),
        [activeVideos, comparisonVideos, isComparison, cameraViews, singleVideo, t],
    );
    const mediaMatch = useMatchMedia('(max-width: 1024px)');

    const doubleMode = mediaMatch ? 'doubleVertical' : 'doubleHorizontal';

    return (
        <div className={css.replayPlayer[variant === mediaVariants.DOUBLE ? doubleMode : 'single']}>
            <Media.Layout
                rows={videoCount === 1 || (!mediaMatch && videoCount === 2) ? 1 : 2}
                cols={videoCount === 1 || (mediaMatch && videoCount === 2) ? 1 : 2}
            >
                {videos.map((video, index) => (
                    <PlaybackCell
                        key={video.cameraId + index}
                        {...video}
                        onChangeView={
                            video.type === VIDEO_TYPE.COMPARISON && !singleVideo
                                ? null
                                : (view) =>
                                      setCameraViews((prev) => {
                                          const next: [number, number, number, number] = [...prev];
                                          next[video.viewIndex] = view;
                                          return next;
                                      })
                        }
                    />
                ))}
            </Media.Layout>
        </div>
    );
}
